import { PALETTE, sline, scircle, sellipse, srect, spoly, fillPoly, hatch, sarc, rand, wob } from './sketch.js';

/**
 * 家中器物 / 庭院景物的简笔画绘制。
 * 所有函数签名统一：(ctx, x, y, w, h, prop, view)
 *   x, y 为该物件占据矩形的左上角像素坐标；w, h 为像素尺寸。
 * 约定：视觉上「立」在矩形底边，因此底边 = 落地基准线。
 */

function baseShadow(ctx, x, y, w, h, k = 0.8) {
  ctx.save();
  ctx.globalAlpha = 0.14;
  ctx.fillStyle = '#000';
  ctx.beginPath();
  ctx.ellipse(x + w / 2, y + h - 2, w * 0.42 * k, w * 0.14 * k, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

export const PROP_ART = {
  /* ---------- 室内 ---------- */
  bed(ctx, x, y, w, h, p) {
    const bx = x + 3, by = y + 4, bw = w - 6, bh = h - 8;
    // 床板
    fillPoly(ctx, [[bx, by], [bx + bw, by], [bx + bw, by + bh], [bx, by + bh]], '#e7d9bd');
    srect(ctx, bx, by, bw, bh, { color: PALETTE.woodDark, width: 1.6, seed: p.gx * 13 + p.gy });
    // 被子（朱砂色，带褶皱）
    const qy = by + bh * 0.42;
    fillPoly(ctx, [[bx + 1, qy], [bx + bw - 1, qy], [bx + bw - 1, by + bh - 1], [bx + 1, by + bh - 1]], PALETTE.cloth, 0.55);
    sline(ctx, bx + 1, qy, bx + bw - 1, qy, { color: PALETTE.cloth, width: 1.4, seed: p.gx + 5, amp: 1.6 });
    sline(ctx, bx + bw * 0.4, qy + 2, bx + bw * 0.4, by + bh - 2, { color: PALETTE.inkSoft, width: 0.8, alpha: 0.4, seed: p.gx + 9, amp: 1 });
    // 枕头
    fillPoly(ctx, [[bx + 3, by + 3], [bx + bw * 0.3, by + 3], [bx + bw * 0.3, by + 10], [bx + 3, by + 10]], '#fbf6ea');
    sellipse(ctx, bx + bw * 0.16, by + 6.5, bw * 0.13, 4, { color: PALETTE.ink, width: 1, seed: p.gx + 3 });
    // 床头
    sline(ctx, bx, by - 2, bx, by + bh, { color: PALETTE.woodDark, width: 2.6, seed: p.gx + 21 });
  },

  desk(ctx, x, y, w, h, p) {
    const bx = x + 4, by = y + h * 0.3, bw = w - 8, bh = h * 0.5;
    fillPoly(ctx, [[bx, by], [bx + bw, by], [bx + bw, by + bh * 0.4], [bx, by + bh * 0.4]], '#e3cba2');
    srect(ctx, bx, by, bw, bh * 0.4, { color: PALETTE.woodDark, width: 1.4, seed: p.gx * 7 + 1 });
    // 桌腿
    sline(ctx, bx + 3, by + bh * 0.4, bx + 3, y + h - 2, { color: PALETTE.woodDark, width: 1.6, seed: p.gx + 31 });
    sline(ctx, bx + bw - 3, by + bh * 0.4, bx + bw - 3, y + h - 2, { color: PALETTE.woodDark, width: 1.6, seed: p.gx + 32 });
    // 桌上：一卷书 + 一盏灯
    fillPoly(ctx, [[bx + 4, by - 5], [bx + 15, by - 5], [bx + 15, by + 1], [bx + 4, by + 1]], '#f3ead6');
    srect(ctx, bx + 4, by - 5, 11, 6, { color: PALETTE.inkSoft, width: 0.9, seed: p.gx + 41 });
    scircle(ctx, bx + bw - 8, by - 4, 3.6, { color: PALETTE.gold, width: 1.2, seed: p.gx + 51, fill: 'rgba(217,180,91,0.5)' });
  },

  stool(ctx, x, y, w, h, p) {
    const cx = x + w / 2;
    sellipse(ctx, cx, y + h * 0.45, w * 0.3, w * 0.15, { color: PALETTE.woodDark, width: 1.4, seed: p.gx + 3, fill: '#e3cba2' });
    sline(ctx, cx - w * 0.2, y + h * 0.5, cx - w * 0.24, y + h - 2, { color: PALETTE.woodDark, width: 1.4, seed: p.gx + 7 });
    sline(ctx, cx + w * 0.2, y + h * 0.5, cx + w * 0.24, y + h - 2, { color: PALETTE.woodDark, width: 1.4, seed: p.gx + 8 });
  },

  stove(ctx, x, y, w, h, p) {
    const bx = x + 4, by = y + h * 0.3, bw = w - 8, bh = h * 0.62;
    fillPoly(ctx, [[bx, by], [bx + bw, by], [bx + bw, by + bh], [bx, by + bh]], '#cbb9a4');
    srect(ctx, bx, by, bw, bh, { color: PALETTE.ink, width: 1.6, seed: p.gx * 3 + 2 });
    hatch(ctx, bx + 1, by + 1, bw - 2, bh - 2, { gap: 6, alpha: 0.18, seed: p.gx });
    // 灶眼 + 锅
    sellipse(ctx, bx + bw / 2, by + 1, bw * 0.3, 3.4, { color: PALETTE.ink, width: 1.2, seed: p.gx + 11, fill: '#5b534b' });
    // 灶口（火）
    fillPoly(ctx, [[bx + bw * 0.25, by + bh * 0.55], [bx + bw * 0.75, by + bh * 0.55], [bx + bw * 0.75, by + bh], [bx + bw * 0.25, by + bh]], '#8d8377');
    sline(ctx, bx + bw * 0.4, by + bh * 0.95, bx + bw * 0.45, by + bh * 0.7, { color: '#d9705e', width: 1.6, seed: p.gx + 13 });
    sline(ctx, bx + bw * 0.6, by + bh * 0.95, bx + bw * 0.57, by + bh * 0.72, { color: '#e0a13f', width: 1.4, seed: p.gx + 14 });
    // 烟囱
    sline(ctx, bx + bw - 4, by, bx + bw - 4, y + 2, { color: PALETTE.ink, width: 2.4, seed: p.gx + 17 });
  },

  shelf(ctx, x, y, w, h, p) {
    const bx = x + 3, by = y + 2, bw = w - 6, bh = h - 6;
    fillPoly(ctx, [[bx, by], [bx + bw, by], [bx + bw, by + bh], [bx, by + bh]], '#dcbf94');
    srect(ctx, bx, by, bw, bh, { color: PALETTE.woodDark, width: 1.6, seed: p.gx * 11 });
    // 两层隔板
    for (let i = 1; i <= 2; i++) {
      const ly = by + (bh / 3) * i;
      sline(ctx, bx, ly, bx + bw, ly, { color: PALETTE.woodDark, width: 1.2, seed: p.gx * 5 + i, amp: 0.6 });
      // 书
      for (let b = 0; b < 3; b++) {
        const bx2 = bx + 3 + b * 7;
        if (bx2 + 5 > bx + bw - 2) break;
        fillPoly(ctx, [[bx2, ly - 9], [bx2 + 5, ly - 9], [bx2 + 5, ly], [bx2, ly]], ['#a2743f', '#6d8fa8', '#8f6d5a'][(b + p.gx) % 3], 0.85);
        sline(ctx, bx2, ly - 9, bx2, ly, { color: PALETTE.ink, width: 0.8, alpha: 0.5, seed: bx2 + p.gy });
      }
    }
  },

  jar(ctx, x, y, w, h, p) {
    const cx = x + w / 2;
    const top = y + h * 0.35;
    const bot = y + h - 3;
    // 缸身（梯形）
    fillPoly(ctx, [[cx - w * 0.26, top], [cx + w * 0.26, top], [cx + w * 0.2, bot], [cx - w * 0.2, bot]], '#b9c6c9');
    spoly(ctx, [[cx - w * 0.26, top], [cx + w * 0.26, top], [cx + w * 0.2, bot], [cx - w * 0.2, bot]], { color: PALETTE.ink, width: 1.6, seed: p.gx * 17 });
    // 水面
    sellipse(ctx, cx, top + 2, w * 0.24, 3, { color: PALETTE.water, width: 1.1, seed: p.gx + 5, fill: 'rgba(143,184,196,0.6)' });
    sline(ctx, cx - w * 0.22, top + (bot - top) * 0.5, cx + w * 0.22, top + (bot - top) * 0.5, { color: PALETTE.inkSoft, width: 1, alpha: 0.5, seed: p.gx + 9 });
  },

  chest(ctx, x, y, w, h, p) {
    const bx = x + 4, by = y + h * 0.35, bw = w - 8, bh = h * 0.55;
    fillPoly(ctx, [[bx, by], [bx + bw, by], [bx + bw, by + bh], [bx, by + bh]], '#d3ab74');
    srect(ctx, bx, by, bw, bh, { color: PALETTE.woodDark, width: 1.6, seed: p.gx * 19 });
    sline(ctx, bx, by + bh * 0.45, bx + bw, by + bh * 0.45, { color: PALETTE.woodDark, width: 1.2, seed: p.gx + 3 });
    scircle(ctx, bx + bw / 2, by + bh * 0.45, 2.4, { color: PALETTE.gold, width: 1.2, seed: p.gx + 7, fill: PALETTE.gold });
    // 箱盖弧
    sarc(ctx, bx + bw / 2, by, bw / 2, Math.PI, 0, { color: PALETTE.woodDark, width: 1.4 });
  },

  cushion(ctx, x, y, w, h, p) {
    const cx = x + w / 2, cy = y + h * 0.7;
    fillPoly(ctx, [[cx - w * 0.28, cy], [cx, cy - h * 0.16], [cx + w * 0.28, cy], [cx, cy + h * 0.16]], '#d9c9a8');
    sellipse(ctx, cx, cy, w * 0.28, h * 0.12, { color: PALETTE.inkSoft, width: 1.4, seed: p.gx * 23 });
    for (let i = -1; i <= 1; i++) {
      sline(ctx, cx, cy - h * 0.12, cx + i * w * 0.2, cy + h * 0.1, { color: PALETTE.inkSoft, width: 0.8, alpha: 0.5, seed: p.gx + i });
    }
  },

  rug(ctx, x, y, w, h, p) {
    const bx = x + 3, by = y + h * 0.45, bw = w - 6, bh = h * 0.4;
    fillPoly(ctx, [[bx, by], [bx + bw, by], [bx + bw, by + bh], [bx, by + bh]], 'rgba(217,112,94,0.35)');
    sellipse(ctx, x + w / 2, y + h * 0.65, bw * 0.5, bh * 0.5, { color: PALETTE.cloth, width: 1.4, seed: p.gx * 29, alpha: 0.8 });
    sellipse(ctx, x + w / 2, y + h * 0.65, bw * 0.32, bh * 0.32, { color: PALETTE.cloth, width: 1, alpha: 0.6, seed: p.gx + 3 });
  },

  lamp(ctx, x, y, w, h, p) {
    const cx = x + w / 2;
    const by = y + h - 4;
    sline(ctx, cx, by, cx, y + h * 0.5, { color: PALETTE.woodDark, width: 1.8, seed: p.gx + 3 });
    fillPoly(ctx, [[cx - 7, y + h * 0.5], [cx + 7, y + h * 0.5], [cx + 5, y + h * 0.24], [cx - 5, y + h * 0.24]], 'rgba(224,161,63,0.55)');
    spoly(ctx, [[cx - 7, y + h * 0.5], [cx + 7, y + h * 0.5], [cx + 5, y + h * 0.24], [cx - 5, y + h * 0.24]], { color: PALETTE.cloth, width: 1.4, seed: p.gx * 31 });
    sline(ctx, cx - 7, y + h * 0.5, cx + 7, y + h * 0.5, { color: PALETTE.cloth, width: 1.2, seed: p.gx + 5 });
    if (p.state && p.state.lit) {
      ctx.save();
      ctx.globalAlpha = 0.35;
      const g = ctx.createRadialGradient(cx, y + h * 0.37, 1, cx, y + h * 0.37, 26);
      g.addColorStop(0, '#ffdd8a');
      g.addColorStop(1, 'rgba(255,221,138,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(cx, y + h * 0.37, 26, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  },

  tv(ctx, x, y, w, h, p, view) {
    const bx = x + 3, by = y + h * 0.26, bw = w - 6, bh = h * 0.56;
    const on = !!(p.state && p.state.url);
    // 天线
    sline(ctx, x + w * 0.38, by, x + w * 0.16, y + 3, { color: PALETTE.ink, width: 1.2, seed: p.gx + 3 });
    sline(ctx, x + w * 0.62, by, x + w * 0.84, y + 6, { color: PALETTE.ink, width: 1.2, seed: p.gx + 4 });
    scircle(ctx, x + w * 0.16, y + 3, 1.6, { color: PALETTE.ink, width: 1, fill: PALETTE.ink, seed: p.gx + 5 });
    scircle(ctx, x + w * 0.84, y + 6, 1.6, { color: PALETTE.ink, width: 1, fill: PALETTE.ink, seed: p.gx + 6 });
    // 木壳
    fillPoly(ctx, [[bx, by], [bx + bw, by], [bx + bw, by + bh], [bx, by + bh]], '#8d6a45');
    srect(ctx, bx, by, bw, bh, { color: PALETTE.woodDark, width: 1.6, seed: p.gx * 11 + 1 });
    // 屏幕
    const sx = bx + 3, sy = by + 3, sw = bw * 0.64, sh = bh - 6;
    fillPoly(ctx, [[sx, sy], [sx + sw, sy], [sx + sw, sy + sh], [sx, sy + sh]], on ? '#2f3d4d' : '#cdc8bc');
    if (on) {
      const t = view ? view.time : 0;
      const gy = sy + ((t * 14 + p.gx * 7) % sh);
      ctx.save();
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = PALETTE.water;
      ctx.fillRect(sx + 1, gy, sw - 2, 2.5);
      ctx.globalAlpha = 0.4 + 0.3 * Math.sin(t * 3 + p.gx);
      ctx.fillStyle = PALETTE.gold;
      ctx.fillRect(sx + sw * 0.25, sy + sh * 0.3, sw * 0.2, 1.6);
      ctx.fillRect(sx + sw * 0.5, sy + sh * 0.62, sw * 0.3, 1.6);
      ctx.restore();
    } else {
      ctx.save();
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = PALETTE.inkSoft;
      for (let i = 0; i < 7; i++) {
        ctx.fillRect(sx + 2 + rand(p.gx * 31 + i) * (sw - 4), sy + 2 + rand(p.gy * 17 + i) * (sh - 4), 1.2, 1.2);
      }
      ctx.restore();
    }
    srect(ctx, sx, sy, sw, sh, { color: PALETTE.ink, width: 1.2, seed: p.gx + 7 });
    // 旋钮与支腿
    scircle(ctx, bx + bw - 6, sy + 4, 2.2, { color: PALETTE.ink, width: 1, fill: '#e0d5bd', seed: p.gx + 8 });
    scircle(ctx, bx + bw - 6, sy + 11, 2.2, { color: PALETTE.ink, width: 1, fill: '#e0d5bd', seed: p.gx + 9 });
    sline(ctx, bx + 4, by + bh, bx + 3, y + h - 2, { color: PALETTE.woodDark, width: 1.6, seed: p.gx + 41 });
    sline(ctx, bx + bw - 4, by + bh, bx + bw - 3, y + h - 2, { color: PALETTE.woodDark, width: 1.6, seed: p.gx + 42 });
  },

  /** 白板：立着的一块板，钉着随手写的便签；写了几条就有几张纸 */
  whiteboard(ctx, x, y, w, h, p) {
    const bx = x + w * 0.1, by = y + h * 0.08, bw = w * 0.8, bh = h * 0.6;
    // 两条支腿
    sline(ctx, x + w * 0.3, by + bh, x + w * 0.2, y + h - 2, { color: PALETTE.woodDark, width: 1.8, seed: p.gx + 51 });
    sline(ctx, x + w * 0.7, by + bh, x + w * 0.8, y + h - 2, { color: PALETTE.woodDark, width: 1.8, seed: p.gx + 52 });
    sline(ctx, x + w * 0.22, y + h - 3, x + w * 0.78, y + h - 3, { color: PALETTE.woodDark, width: 1.2, seed: p.gx + 53 });
    // 板面（木框 + 白底）
    fillPoly(ctx, [[bx, by], [bx + bw, by], [bx + bw, by + bh], [bx, by + bh]], '#fdfaf1');
    srect(ctx, bx, by, bw, bh, { color: PALETTE.woodDark, width: 1.6, seed: p.gx * 17 + 3 });
    // 板上钉的便签：记了就有，最多露五张
    const notes = p.state && Array.isArray(p.state.notes) ? p.state.notes : [];
    const count = Math.max(1, Math.min(5, notes.length || 1));
    const tint = ['#f2dca6', '#cfe0d6', '#e8c9c4', '#d9d4ea', '#dce8f0'];
    const cw = bw * 0.4, ch = bh * 0.26;
    for (let i = 0; i < count; i++) {
      const col = i % 2, row = Math.floor(i / 2);
      const nx = bx + (col ? bw - cw - bw * 0.06 : bw * 0.06);
      const ny = by + bh * 0.08 + row * (ch + bh * 0.07);
      if (ny + ch > by + bh - 2) break;
      const c = tint[(p.gx + i) % tint.length];
      fillPoly(ctx, [[nx, ny], [nx + cw, ny], [nx + cw, ny + ch], [nx, ny + ch]], c, 0.92);
      srect(ctx, nx, ny, cw, ch, { color: PALETTE.inkSoft, width: 0.8, alpha: 0.5, seed: p.gx * 29 + i });
      // 纸上的两行字
      ctx.save();
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = PALETTE.ink;
      for (let k = 0; k < 2; k++) {
        const lw = cw * (0.4 + rand(p.gx * 37 + i * 5 + k) * 0.45);
        ctx.fillRect(nx + 2, ny + 3 + k * (ch * 0.42), lw, 1);
      }
      ctx.restore();
      // 图钉
      scircle(ctx, nx + cw / 2, ny + 1.5, 1.3, { color: PALETTE.cloth, width: 0.8, fill: PALETTE.cloth, seed: p.gx + 60 + i });
    }
    // 板下沿的一道笔槽
    sline(ctx, bx + 2, by + bh - 2.5, bx + bw - 2, by + bh - 2.5, { color: PALETTE.woodDark, width: 1.4, seed: p.gx + 61 });
  },

  /** 游戏桌：一张方桌四条凳，桌面嵌着和电视一样的那方小窗 */
  gametable(ctx, x, y, w, h, p, view) {
    const cx = x + w / 2;
    const on = !!(p.state && p.state.url);

    // 四条凳子：南北各两张，搁在桌边外一点，人正好坐下
    const bw = w * 0.19, bh = h * 0.075;
    for (const [sx, sy, sd] of [
      [cx - w * 0.23, y + h * 0.1, 1], [cx + w * 0.23, y + h * 0.1, 2],
      [cx - w * 0.23, y + h * 0.9, 3], [cx + w * 0.23, y + h * 0.9, 4],
    ]) {
      fillPoly(ctx, [[sx - bw / 2, sy - bh / 2], [sx + bw / 2, sy - bh / 2],
        [sx + bw / 2, sy + bh / 2], [sx - bw / 2, sy + bh / 2]], '#a8814f');
      srect(ctx, sx - bw / 2, sy - bh / 2, bw, bh, { color: PALETTE.woodDark, width: 1.1, seed: p.gx * 13 + sd });
      sline(ctx, sx - bw / 2 + 2, sy + bh / 2, sx - bw / 2 + 1, sy + bh * 1.7,
        { color: PALETTE.woodDark, width: 1.2, seed: p.gx * 17 + sd });
      sline(ctx, sx + bw / 2 - 2, sy + bh / 2, sx + bw / 2 - 1, sy + bh * 1.7,
        { color: PALETTE.woodDark, width: 1.2, seed: p.gy * 19 + sd });
    }

    // 桌面
    const tw = w - 14, th = h * 0.5;
    const tx = cx - tw / 2, ty = y + h * 0.25;
    fillPoly(ctx, [[tx, ty], [tx + tw, ty], [tx + tw, ty + th], [tx, ty + th]], '#8d6a45');
    srect(ctx, tx, ty, tw, th, { color: PALETTE.woodDark, width: 1.6, seed: p.gx * 11 + 1 });

    // 桌中央的小窗：没开台是块淡棋盘，开台了就亮起来
    const px = tx + 5, py = ty + 5, pw = tw - 10, ph = th - 10;
    fillPoly(ctx, [[px, py], [px + pw, py], [px + pw, py + ph], [px, py + ph]], on ? '#2f3d4d' : '#ded6c4');
    if (on) {
      const t = view ? view.time : 0;
      const gy = py + ((t * 14 + p.gx * 7) % ph);
      ctx.save();
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = PALETTE.water;
      ctx.fillRect(px + 1, gy, pw - 2, 2.2);
      ctx.globalAlpha = 0.4 + 0.3 * Math.sin(t * 3 + p.gx);
      ctx.fillStyle = PALETTE.gold;
      ctx.fillRect(px + pw * 0.28, py + ph * 0.3, pw * 0.22, 1.5);
      ctx.fillRect(px + pw * 0.5, py + ph * 0.62, pw * 0.28, 1.5);
      ctx.restore();
    } else {
      // 淡淡的棋盘格，看着像能摆两局
      ctx.save();
      ctx.globalAlpha = 0.22;
      ctx.fillStyle = PALETTE.inkSoft;
      const n = 4, cw = pw / n, chh = ph / n;
      for (let r = 0; r < n; r++) {
        for (let c = 0; c < n; c++) {
          if ((r + c) % 2) continue;
          ctx.fillRect(px + c * cw, py + r * chh, cw, chh);
        }
      }
      ctx.restore();
    }
    srect(ctx, px, py, pw, ph, { color: PALETTE.ink, width: 1.2, seed: p.gx + 7 });

    // 四条桌腿
    for (const [lx, ly] of [[tx + 4, tx + 3], [tx + tw - 4, tx + tw - 3]]) {
      sline(ctx, lx, ty + th, ly, y + h - 3, { color: PALETTE.woodDark, width: 1.6, seed: p.gx * 23 + lx });
    }
  },

  /* ---------- 庭院 ---------- */
  well(ctx, x, y, w, h, p) {
    const cx = x + w / 2, cy = y + h * 0.68;
    const rx = w * 0.3, ry = h * 0.16;
    fillPoly(ctx, [[cx - rx, cy], [cx, cy - ry], [cx + rx, cy], [cx, cy + ry]], '#c9c3b7');
    sellipse(ctx, cx, cy, rx, ry, { color: PALETTE.ink, width: 1.8, seed: p.gx * 37, fill: '#9aa0a0' });
    sellipse(ctx, cx, cy, rx * 0.62, ry * 0.62, { color: PALETTE.ink, width: 1.2, seed: p.gx + 5, fill: '#3f4a4d' });
    // 架子 + 桶
    sline(ctx, cx - rx, cy - ry * 0.4, cx - rx * 0.9, y + 4, { color: PALETTE.woodDark, width: 2, seed: p.gx + 11 });
    sline(ctx, cx + rx, cy - ry * 0.4, cx + rx * 0.9, y + 4, { color: PALETTE.woodDark, width: 2, seed: p.gx + 12 });
    sline(ctx, cx - rx * 0.9, y + 4, cx + rx * 0.9, y + 4, { color: PALETTE.woodDark, width: 2, seed: p.gx + 13 });
    sline(ctx, cx, y + 4, cx, y + h * 0.35, { color: PALETTE.inkSoft, width: 1, seed: p.gx + 14 });
    srect(ctx, cx - 3.5, y + h * 0.35, 7, 6, { color: PALETTE.woodDark, width: 1.2, seed: p.gx + 15, fill: '#c79a63' });
  },

  tree(ctx, x, y, w, h, p) {
    const cx = x + w / 2;
    const by = y + h - 4;
    // 树干
    fillPoly(ctx, [[cx - 3, by], [cx + 3, by], [cx + 2, y + h * 0.35], [cx - 2, y + h * 0.35]], '#a2743f');
    sline(ctx, cx - 3, by, cx - 2, y + h * 0.35, { color: PALETTE.woodDark, width: 1.6, seed: p.gx + 3 });
    sline(ctx, cx + 3, by, cx + 2, y + h * 0.35, { color: PALETTE.woodDark, width: 1.6, seed: p.gx + 4 });
    sline(ctx, cx - 2, y + h * 0.5, cx - 8, y + h * 0.35, { color: PALETTE.woodDark, width: 1.2, seed: p.gx + 5 });
    sline(ctx, cx + 2, y + h * 0.5, cx + 8, y + h * 0.34, { color: PALETTE.woodDark, width: 1.2, seed: p.gx + 6 });
    // 树冠：三团重叠云线
    const cy = y + h * 0.26;
    const blobs = [[-8, 2, 11], [8, 0, 12], [0, -8, 13]];
    ctx.save();
    ctx.globalAlpha = 0.75;
    fillPoly(ctx, [[cx - 18, cy + 6], [cx - 10, cy - 12], [cx + 4, cy - 18], [cx + 18, cy - 6], [cx + 16, cy + 8], [cx, cy + 12]], 'rgba(143,174,107,0.85)');
    ctx.restore();
    for (const [dx, dy, r] of blobs) {
      scircle(ctx, cx + dx, cy + dy, r, { color: PALETTE.leafDark, width: 1.5, seed: p.gx * 7 + dx, amp: 1.6, seg: 11 });
    }
    // 果子
    if (p.state && p.state.fruit) {
      for (let i = 0; i < 3; i++) {
        scircle(ctx, cx - 8 + i * 9, cy - 4 + (i % 2) * 8, 2.2, { color: PALETTE.cloth, width: 1, seed: p.gx + i + 40, fill: PALETTE.cloth });
      }
    }
  },

  plot(ctx, x, y, w, h, p) {
    const stage = (p.state && p.state.stage) || 0; // 0 空 1 苗 2 长 3 熟
    const bx = x + 4, by = y + h * 0.42, bw = w - 8, bh = h * 0.5;
    fillPoly(ctx, [[bx, by], [bx + bw, by], [bx + bw, by + bh], [bx, by + bh]], 'rgba(180,131,86,0.5)');
    srect(ctx, bx, by, bw, bh, { color: PALETTE.soilDark, width: 1.2, alpha: 0.7, seed: p.gx * 41 });
    for (let i = 0; i < 3; i++) {
      sline(ctx, bx + 2, by + bh * (0.25 + i * 0.25), bx + bw - 2, by + bh * (0.25 + i * 0.25), { color: PALETTE.soilDark, width: 0.8, alpha: 0.5, seed: p.gx + i });
    }
    if (stage > 0) {
      const n = 3;
      for (let i = 0; i < n; i++) {
        const px2 = bx + bw * (0.22 + i * 0.28);
        const py2 = by + bh * 0.6;
        const tall = stage === 1 ? 4 : stage === 2 ? 9 : 12;
        sline(ctx, px2, py2, px2, py2 - tall, { color: PALETTE.leafDark, width: 1.4, seed: p.gx * 13 + i, amp: 0.8 });
        if (stage >= 2) {
          sline(ctx, px2, py2 - tall * 0.6, px2 - 5, py2 - tall * 0.85, { color: PALETTE.leafDark, width: 1.1, seed: p.gx * 17 + i });
          sline(ctx, px2, py2 - tall * 0.6, px2 + 5, py2 - tall * 0.85, { color: PALETTE.leafDark, width: 1.1, seed: p.gx * 19 + i });
        }
        if (stage >= 3) {
          scircle(ctx, px2, py2 - tall - 2, 2.6, { color: PALETTE.cloth, width: 1.1, seed: p.gx * 23 + i, fill: 'rgba(217,112,94,0.8)' });
        }
      }
    }
  },

  // 水田：一畦水，稻子熟了弯下头（收下来是新谷，舂过才是米）
  paddy_plot(ctx, x, y, w, h, p) {
    const stage = (p.state && p.state.stage) || 0; // 0 空 1 秧 2 长 3 熟
    const bx = x + 4, by = y + h * 0.42, bw = w - 8, bh = h * 0.5;
    fillPoly(ctx, [[bx, by], [bx + bw, by], [bx + bw, by + bh], [bx, by + bh]], 'rgba(150,183,190,0.55)');
    srect(ctx, bx, by, bw, bh, { color: PALETTE.soilDark, width: 1.2, alpha: 0.6, seed: p.gx * 43 });
    for (let i = 0; i < 2; i++) {
      sline(ctx, bx + 2, by + bh * (0.35 + i * 0.3), bx + bw - 2, by + bh * (0.35 + i * 0.3),
        { color: '#8fb0b8', width: 0.8, alpha: 0.7, seed: p.gx + i + 7 });
    }
    if (stage > 0) {
      for (let i = 0; i < 3; i++) {
        const px2 = bx + bw * (0.22 + i * 0.28);
        const py2 = by + bh * 0.6;
        const tall = stage === 1 ? 5 : stage === 2 ? 10 : 14;
        sline(ctx, px2, py2, px2, py2 - tall, { color: PALETTE.leafDark, width: 1.3, seed: p.gx * 13 + i, amp: 0.8 });
        if (stage >= 2) {
          sline(ctx, px2, py2 - tall * 0.6, px2 - 5, py2 - tall * 0.9, { color: PALETTE.leafDark, width: 1, seed: p.gx * 17 + i });
          sline(ctx, px2, py2 - tall * 0.6, px2 + 5, py2 - tall * 0.9, { color: PALETTE.leafDark, width: 1, seed: p.gx * 19 + i });
        }
        if (stage >= 3) {
          // 稻穗：熟了就沉甸甸地低下去
          sline(ctx, px2, py2 - tall, px2 + 4, py2 - tall - 5, { color: '#c9a24a', width: 1.4, seed: p.gx * 29 + i });
          scircle(ctx, px2 + 4, py2 - tall - 5, 2.2, { color: '#c9a24a', width: 1, seed: p.gx * 31 + i, fill: 'rgba(201,162,74,0.85)' });
        }
      }
    }
  },

  // 米缸：缸里有多少米，就露出来多少
  rice_bin(ctx, x, y, w, h, p) {
    const cx = x + w / 2;
    const top = y + h * 0.3;
    const bot = y + h - 3;
    const tw = w * 0.42, bw = w * 0.34;
    fillPoly(ctx, [[cx - tw, top], [cx + tw, top], [cx + bw, bot], [cx - bw, bot]], '#e6dcc6');
    sline(ctx, cx - tw, top, cx + tw, top, { color: PALETTE.ink, width: 1.6, seed: p.gx });
    sline(ctx, cx - tw, top, cx - bw, bot, { color: PALETTE.ink, width: 1.4, seed: p.gx + 1 });
    sline(ctx, cx + tw, top, cx + bw, bot, { color: PALETTE.ink, width: 1.4, seed: p.gx + 2 });
    sline(ctx, cx - bw, bot, cx + bw, bot, { color: PALETTE.ink, width: 1.2, seed: p.gx + 3 });
    const rice = (p.state && p.state.rice) || 0;
    if (rice > 0) {
      const lvl = Math.min(1, rice / 12);
      const ry = bot - (bot - top) * (0.12 + 0.5 * lvl);
      fillPoly(ctx, [[cx - bw * 0.8, ry], [cx + bw * 0.8, ry], [cx + bw * 0.86, bot - 1], [cx - bw * 0.86, bot - 1]], 'rgba(240,235,216,0.95)');
      sline(ctx, cx - bw * 0.8, ry, cx + bw * 0.8, ry, { color: PALETTE.soilDark, width: 1, alpha: 0.5, seed: p.gx + 4 });
    }
  },

  fence(ctx, x, y, w, h, p) {
    const by = y + h - 3;
    sline(ctx, x + 2, by, x + w - 2, by, { color: PALETTE.woodDark, width: 1.4, alpha: 0.5, seed: p.gx });
    for (let i = 0; i < 3; i++) {
      const px2 = x + 6 + i * (w - 12) / 2;
      sline(ctx, px2, by, px2, y + h * 0.3, { color: PALETTE.woodDark, width: 2, seed: p.gx * 5 + i, amp: 0.8 });
    }
    sline(ctx, x + 2, y + h * 0.55, x + w - 2, y + h * 0.55, { color: PALETTE.woodDark, width: 1.4, seed: p.gx + 9 });
  },

  stone_lantern(ctx, x, y, w, h, p) {
    const cx = x + w / 2;
    const by = y + h - 2;
    srect(ctx, cx - 5, by - 4, 10, 4, { color: PALETTE.ink, width: 1.4, seed: p.gx + 1, fill: '#cfc9bd' });
    sline(ctx, cx, by - 4, cx, by - 14, { color: PALETTE.ink, width: 3, seed: p.gx + 2 });
    srect(ctx, cx - 6, by - 20, 12, 6, { color: PALETTE.ink, width: 1.4, seed: p.gx + 3, fill: '#e6e0d4' });
    spoly(ctx, [[cx - 8, by - 20], [cx + 8, by - 20], [cx + 5, by - 27], [cx - 5, by - 27]], { color: PALETTE.ink, width: 1.4, seed: p.gx + 4, fill: '#cfc9bd' });
    if (p.state && p.state.lit) {
      ctx.save();
      ctx.globalAlpha = 0.3;
      const g = ctx.createRadialGradient(cx, by - 17, 1, cx, by - 17, 24);
      g.addColorStop(0, '#ffdd8a');
      g.addColorStop(1, 'rgba(255,221,138,0)');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(cx, by - 17, 24, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
  },

  sign(ctx, x, y, w, h, p) {
    const cx = x + w / 2;
    sline(ctx, cx, y + h - 2, cx, y + h * 0.45, { color: PALETTE.woodDark, width: 2, seed: p.gx });
    fillPoly(ctx, [[cx - 12, y + h * 0.2], [cx + 12, y + h * 0.2], [cx + 12, y + h * 0.45], [cx - 12, y + h * 0.45]], '#e3cba2');
    srect(ctx, cx - 12, y + h * 0.2, 24, h * 0.25, { color: PALETTE.woodDark, width: 1.4, seed: p.gx + 3 });
    if (p.state && p.state.text) {
      ctx.save();
      ctx.fillStyle = PALETTE.ink;
      ctx.font = '10px "LXGW WenKai Lite","Kaiti SC","STKaiti",serif';
      ctx.textAlign = 'center';
      ctx.fillText(String(p.state.text).slice(0, 3), cx, y + h * 0.35);
      ctx.restore();
    }
  },
  /* ------------------- 城郊与城市新增器物 ------------------- */

  /** 门户：一块发光的界石，脚下一圈提示 */
  portal(ctx, x, y, w, h, p) {
    const cx = x + w / 2, by = y + h;
    spoly(ctx, [[cx - 13, by - 2], [cx + 13, by - 2], [cx + 9, by - 26], [cx - 9, by - 26]], { color: PALETTE.ink, width: 1.6, seed: p.gx + 1, fill: '#dfd8c8' });
    sline(ctx, cx, by - 26, cx, by - 2, { color: PALETTE.inkSoft, width: 0.9, alpha: 0.5, seed: p.gx + 2 });
    ctx.save();
    ctx.globalAlpha = 0.35;
    const g = ctx.createRadialGradient(cx, by - 14, 1, cx, by - 14, 22);
    g.addColorStop(0, '#cfe4d6'); g.addColorStop(1, 'rgba(207,228,214,0)');
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(cx, by - 14, 22, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    const to = p.state && p.state.to;
    if (to) {
      ctx.save();
      ctx.fillStyle = PALETTE.ink; ctx.globalAlpha = 0.55;
      ctx.font = '9px "LXGW WenKai Lite","Kaiti SC","STKaiti",serif';
      ctx.textAlign = 'center';
      ctx.fillText(String(p.state.label || to).slice(0, 4), cx, by - 30);
      ctx.restore();
    }
  },

  junk_pile(ctx, x, y, w, h, p) {
    const by = y + h;
    spoly(ctx, [[x + 2, by], [x + w * 0.3, y + h * 0.35], [x + w * 0.62, y + h * 0.2], [x + w - 2, by]], { color: PALETTE.inkSoft, width: 1.2, alpha: 0.6, seed: p.gx, fill: '#c9bda6' });
    for (let i = 0; i < 4; i++) {
      const px2 = x + 6 + rand(p.gx + i) * (w - 12);
      const py2 = y + h * 0.45 + rand(p.gx + i * 7) * h * 0.4;
      scircle(ctx, px2, py2, 2, { color: PALETTE.inkSoft, width: 0.9, alpha: 0.45, seed: p.gx + i, seg: 5 });
    }
    sline(ctx, x + w * 0.2, by - 6, x + w * 0.75, y + h * 0.4, { color: PALETTE.inkSoft, width: 1, alpha: 0.4, seed: p.gx + 3, amp: 1 });
  },

  workbench(ctx, x, y, w, h, p) {
    const by = y + h;
    srect(ctx, x + 2, y + h * 0.42, w - 4, h * 0.22, { color: PALETTE.woodDark, width: 1.4, seed: p.gx + 1, fill: '#e3cba2' });
    for (const dx of [5, w - 9]) sline(ctx, x + dx, y + h * 0.64, x + dx, by - 1, { color: PALETTE.woodDark, width: 2.4, seed: p.gx + dx });
    // 台面上的零件与工具
    srect(ctx, x + w * 0.28, y + h * 0.24, w * 0.2, h * 0.18, { color: PALETTE.ink, width: 1.1, seed: p.gx + 5, fill: '#c9c3b6' });
    sline(ctx, x + w * 0.62, y + h * 0.4, x + w * 0.8, y + h * 0.16, { color: PALETTE.ink, width: 1.6, seed: p.gx + 6 });
    scircle(ctx, x + w * 0.82, y + h * 0.15, 2.2, { color: PALETTE.ink, width: 1, alpha: 0.7, seed: p.gx + 7, seg: 6 });
  },

  gas_pump(ctx, x, y, w, h, p) {
    const cx = x + w / 2, by = y + h;
    srect(ctx, cx - 9, y + h * 0.18, 18, h * 0.72, { color: PALETTE.ink, width: 1.4, seed: p.gx + 1, fill: '#d76f52' });
    srect(ctx, cx - 6, y + h * 0.24, 12, h * 0.16, { color: PALETTE.ink, width: 1.1, seed: p.gx + 2, fill: '#f4efe2' });
    srect(ctx, cx - 12, by - 5, 24, 5, { color: PALETTE.ink, width: 1.2, seed: p.gx + 3, fill: '#b9b3a6' });
    sline(ctx, cx + 9, y + h * 0.42, cx + 16, y + h * 0.3, { color: PALETTE.ink, width: 1.6, seed: p.gx + 4 });
  },

  counter(ctx, x, y, w, h, p) {
    const by = y + h;
    srect(ctx, x + 1, y + h * 0.3, w - 2, h * 0.24, { color: PALETTE.woodDark, width: 1.4, seed: p.gx + 1, fill: '#e3cba2' });
    srect(ctx, x + 3, y + h * 0.54, w - 6, h * 0.44, { color: PALETTE.woodDark, width: 1.2, seed: p.gx + 2, fill: '#d8bd93' });
    sline(ctx, x + w * 0.35, y + h * 0.62, x + w * 0.35, by - 2, { color: PALETTE.woodDark, width: 1, alpha: 0.6, seed: p.gx + 3 });
    sline(ctx, x + w * 0.65, y + h * 0.62, x + w * 0.65, by - 2, { color: PALETTE.woodDark, width: 1, alpha: 0.6, seed: p.gx + 4 });
    // 台面上的小秤
    scircle(ctx, x + w * 0.5, y + h * 0.26, 3, { color: PALETTE.ink, width: 1, alpha: 0.6, seed: p.gx + 5, seg: 6 });
  },

  notice_board(ctx, x, y, w, h, p) {
    const cx = x + w / 2, by = y + h;
    sline(ctx, cx - 8, by - 2, cx - 8, y + h * 0.3, { color: PALETTE.woodDark, width: 2, seed: p.gx });
    sline(ctx, cx + 8, by - 2, cx + 8, y + h * 0.3, { color: PALETTE.woodDark, width: 2, seed: p.gx + 1 });
    srect(ctx, cx - 13, y + h * 0.08, 26, h * 0.24, { color: PALETTE.woodDark, width: 1.4, seed: p.gx + 2, fill: '#f4efe2' });
    for (let i = 0; i < 3; i++) {
      srect(ctx, cx - 11, y + h * 0.11 + i * 5, 10, 3.4, { color: PALETTE.inkSoft, width: 0.7, alpha: 0.5, seed: p.gx + i * 3, fill: '#e8dfcb' });
      srect(ctx, cx + 1, y + h * 0.11 + i * 5, 10, 3.4, { color: PALETTE.inkSoft, width: 0.7, alpha: 0.5, seed: p.gx + i * 4, fill: '#e8dfcb' });
    }
  },

  milestone(ctx, x, y, w, h, p) {
    const cx = x + w / 2, by = y + h;
    spoly(ctx, [[cx - 7, by], [cx + 7, by], [cx + 5, y + h * 0.2], [cx - 5, y + h * 0.2]], { color: PALETTE.ink, width: 1.4, seed: p.gx, fill: '#dcd6c8' });
    ctx.save();
    ctx.fillStyle = PALETTE.ink; ctx.globalAlpha = 0.6;
    ctx.font = '9px "LXGW WenKai Lite","Kaiti SC","STKaiti",serif';
    ctx.textAlign = 'center';
    ctx.fillText(String((p.state && p.state.text) || '里').slice(0, 2), cx, y + h * 0.42);
    ctx.restore();
  },

  signpost(ctx, x, y, w, h, p) {
    const cx = x + w / 2;
    sline(ctx, cx, y + h, cx, y + h * 0.3, { color: PALETTE.woodDark, width: 2.4, seed: p.gx });
    spoly(ctx, [[cx - 13, y + h * 0.1], [cx + 13, y + h * 0.16], [cx + 13, y + h * 0.34], [cx - 13, y + h * 0.28]], { color: PALETTE.woodDark, width: 1.3, seed: p.gx + 1, fill: '#e3cba2' });
  },

  crate_stack(ctx, x, y, w, h, p) {
    const by = y + h;
    srect(ctx, x + 1, y + h * 0.42, w * 0.52, h * 0.56, { color: PALETTE.woodDark, width: 1.3, seed: p.gx + 1, fill: '#dcbf92' });
    srect(ctx, x + w * 0.44, y + h * 0.16, w * 0.54, h * 0.82, { color: PALETTE.woodDark, width: 1.3, seed: p.gx + 2, fill: '#e8cda2' });
    sline(ctx, x + 1, y + h * 0.68, x + w * 0.53, y + h * 0.68, { color: PALETTE.woodDark, width: 1, alpha: 0.7, seed: p.gx + 3 });
    sline(ctx, x + w * 0.44, by - 4, x + w - 1, by - 4, { color: PALETTE.woodDark, width: 1, alpha: 0.7, seed: p.gx + 4 });
  },

  car_wreck(ctx, x, y, w, h, p) {
    const cx = x + w / 2, by = y + h;
    // 车身歪斜
    spoly(ctx, [[x + 2, by - 4], [x + w - 2, by - 2], [x + w - 4, y + h * 0.5], [x + 4, y + h * 0.46]], { color: PALETTE.ink, width: 1.5, seed: p.gx + 1, fill: '#a89c8c' });
    spoly(ctx, [[x + w * 0.24, y + h * 0.48], [x + w * 0.66, y + h * 0.48], [x + w * 0.6, y + h * 0.22], [x + w * 0.3, y + h * 0.22]], { color: PALETTE.ink, width: 1.3, seed: p.gx + 2, fill: '#c2b6a4' });
    scircle(ctx, x + w * 0.26, by - 3, 4, { color: PALETTE.ink, width: 1.4, alpha: 0.8, seed: p.gx + 3, seg: 7 });
    scircle(ctx, x + w * 0.7, by - 3, 3.4, { color: PALETTE.ink, width: 1.4, alpha: 0.5, seed: p.gx + 4, seg: 7 });
    sline(ctx, cx, by - 4, cx, y + h * 0.3, { color: PALETTE.inkSoft, width: 0.9, alpha: 0.35, seed: p.gx + 5, amp: 1 });
  },
};

/**
 * 兜底画法：数据表里新加的器物还没来得及配专门画法时，
 * 按 def.shape 画一个能认出形状的东西，并在上方标出名字，
 * 保证「加一条数据就能在图里看见」。
 */
export function drawGeneric(ctx, x, y, w, h, prop, _view) {
  const def = prop.def || {};
  const shape = def.shape || 'box';
  const seed = (prop.gx || 0) * 31 + (prop.gy || 0) * 7;
  const by = y + h;
  switch (shape) {
    case 'heap':
      spoly(ctx, [[x + 2, by], [x + w * 0.3, y + h * 0.34], [x + w * 0.66, y + h * 0.18], [x + w - 2, by]], { color: PALETTE.inkSoft, width: 1.2, alpha: 0.6, seed, fill: '#c9bda6' });
      break;
    case 'post':
      srect(ctx, x + w / 2 - 3, y + h * 0.1, 6, h * 0.8, { color: PALETTE.ink, width: 1.3, seed: seed + 1, fill: '#cfc9bd' });
      srect(ctx, x + w / 2 - 7, by - 5, 14, 5, { color: PALETTE.ink, width: 1.2, seed: seed + 2, fill: '#c0b9ac' });
      break;
    case 'counter':
      PROP_ART.counter(ctx, x, y, w, h, prop);
      break;
    case 'barrel': {
      const cx = x + w / 2;
      spoly(ctx, [[cx - w * 0.34, y + h * 0.2], [cx + w * 0.34, y + h * 0.2], [cx + w * 0.28, by], [cx - w * 0.28, by]], { color: PALETTE.ink, width: 1.4, seed, fill: '#d8bd93' });
      sline(ctx, cx - w * 0.32, y + h * 0.46, cx + w * 0.32, y + h * 0.46, { color: PALETTE.woodDark, width: 1.2, alpha: 0.7, seed: seed + 1 });
      sline(ctx, cx - w * 0.3, y + h * 0.72, cx + w * 0.3, y + h * 0.72, { color: PALETTE.woodDark, width: 1.2, alpha: 0.7, seed: seed + 2 });
      break;
    }
    case 'machine':
      srect(ctx, x + 2, y + h * 0.24, w - 4, h * 0.72, { color: PALETTE.ink, width: 1.4, seed, fill: '#c9c3b6' });
      srect(ctx, x + w * 0.2, y + h * 0.32, w * 0.6, h * 0.2, { color: PALETTE.ink, width: 1.1, seed: seed + 1, fill: '#ece6d8' });
      sline(ctx, x + w * 0.8, y + h * 0.55, x + w * 0.95, y + h * 0.4, { color: PALETTE.ink, width: 1.4, seed: seed + 2 });
      break;
    case 'car':
      PROP_ART.car_wreck(ctx, x, y, w, h, prop);
      break;
    case 'board':
      PROP_ART.signpost(ctx, x, y, w, h, prop);
      break;
    case 'pad':
      srect(ctx, x + 1, y + h * 0.55, w - 2, h * 0.42, { color: PALETTE.inkSoft, width: 1.1, alpha: 0.5, seed, fill: '#ddd3bd' });
      break;
    default:
      srect(ctx, x + 2, y + h * 0.3, w - 4, h * 0.66, { color: PALETTE.woodDark, width: 1.3, seed, fill: '#dcbf92' });
      sline(ctx, x + 2, y + h * 0.3, x + w - 2, by - 2, { color: PALETTE.woodDark, width: 1, alpha: 0.5, seed: seed + 1 });
      sline(ctx, x + w - 2, y + h * 0.3, x + 2, by - 2, { color: PALETTE.woodDark, width: 1, alpha: 0.5, seed: seed + 2 });
  }
  // 名字：只有还没有专门画法时才标
  const name = def.name || prop.type;
  if (name) {
    ctx.save();
    ctx.fillStyle = PALETTE.ink;
    ctx.globalAlpha = 0.5;
    ctx.font = '9px "LXGW WenKai Lite","Kaiti SC","STKaiti",serif';
    ctx.textAlign = 'center';
    ctx.fillText(String(name).slice(0, 4), x + w / 2, y - 2);
    ctx.restore();
  }
}

export function drawProp(ctx, prop, view) {
  const fn = PROP_ART[prop.type];
  const x = prop.gx * view.tileSize;
  const y = prop.gy * view.tileSize;
  const w = prop.w * view.tileSize;
  const h = prop.h * view.tileSize;
  baseShadow(ctx, x, y, w, h, prop.shadowScale ?? 1);
  if (fn) fn(ctx, x, y, w, h, prop, view);
  else drawGeneric(ctx, x, y, w, h, prop, view);
}
